/**
 * **Gauntlet** (Core p.271, 8-round cap, one EDZ-fortified CZ) — at the end of round
 * 8, PC win iff (PC count inside the CZ) > (WEIGHTED enemy count inside the CZ);
 * enemy win iff enemy ≥ PCs (tie goes to the enemy, no draw in RAW).
 *
 * **The weight tiers key off `Combatant.templateIds`, NOT `npcf_*` feature ids** — those
 * are FEATURE ids that bake into `extraActivations`/`activationsPctThreshold` and never
 * land on the spawned combatant. `spawn.ts`'s `spawnNpc` freezes the literal content-pack
 * **template ids** `"npct_elite"` / `"npct_ultra"` / `"npct_grunt"` onto the returned
 * `Combatant`. `npct_grunt` is mutually exclusive with `npct_elite`/`npct_ultra`/
 * `npct_veteran` (`npct_grunt.json`'s `prohibitTemplates`), so RAW's four weight tiers
 * (ultra/elite/grunt/standard) are exhaustive and non-overlapping on a real spawn.
 * Standard (no matching template) = 1, matching RAW's unweighted default.
 *
 * `gauntlet.test.ts` pins these weights against ACTUAL `spawnNpc` output over the real v3
 * fixture pack, not a hand-forged `templateIds` array.
 */
import { isCharacter, sideOf, type Combatant, type EncounterState, type SitrepConfig } from "@lancer-vtt/domain";
import { unitsInZone } from "../zones";
import type { SitrepStatus } from "../status";
import { roundLimitReached, roundsRemainingFor } from "../roundBoundary";

const GAUNTLET_ULTRA_WEIGHT = 4;
const GAUNTLET_ELITE_WEIGHT = 2;
const GAUNTLET_GRUNT_WEIGHT = 0.25;
const GAUNTLET_STANDARD_WEIGHT = 1;

/** RAW Gauntlet's per-enemy weight (Core p.271), keyed off the real spawned `templateIds`. */
function enemyWeight(c: Combatant): number {
  const templates = c.templateIds ?? [];
  if (templates.includes("npct_ultra")) return GAUNTLET_ULTRA_WEIGHT;
  if (templates.includes("npct_elite")) return GAUNTLET_ELITE_WEIGHT;
  if (templates.includes("npct_grunt")) return GAUNTLET_GRUNT_WEIGHT;
  return GAUNTLET_STANDARD_WEIGHT;
}

export function gauntletStatus(state: EncounterState, sitrep: SitrepConfig): SitrepStatus {
  const [zone] = sitrep.zones;
  const inZone = zone ? unitsInZone(state, zone).filter(isCharacter) : [];
  const pcCount = inZone.filter((c) => sideOf(c) === "pc").length;
  const enemyWeighted = inZone.filter((c) => sideOf(c) === "npc").reduce((sum, c) => sum + enemyWeight(c), 0);
  const roundsRemaining = roundsRemainingFor(state, sitrep.roundLimit);
  if (!roundLimitReached(state, sitrep.roundLimit)) {
    return { phase: "active", roundsRemaining };
  }
  return { phase: "decided", winner: pcCount > enemyWeighted ? "pc" : "npc" };
}
